import ActionSuccessToast from '../../components/ActionSuccessToast'
import { useEffect, useMemo, useState } from 'react'
import { Copy, Download, Play, RefreshCw } from 'lucide-react'
import { supabase } from '../../lib/supabase.js'
import {
  CARS_CALIBRATION_ANCHORS,
  CARS_CALIBRATION_DIAGNOSTIC_VARIANTS,
  CARS_CALIBRATION_TICKERS,
  CARS_CALIBRATION_VARIANTS,
  buildCarsCalibrationDiagnosticRows,
  runCarsCalibration,
} from '../../lib/chart/carsCalibration.js'

function parseSymbols(value) {
  return String(value || '')
    .split(/[,\s]+/)
    .map((symbol) => symbol.trim().toUpperCase())
    .filter(Boolean)
}

function formatSymbolsText(symbols) {
  return (Array.isArray(symbols) ? symbols : [])
    .map((symbol) => String(symbol || '').trim().toUpperCase())
    .filter(Boolean)
    .join(', ')
}

function formatScore(score) {
  if (!score) return 'n/a'
  return `${score.total} (${score.matchedAnchors}/${score.matchedAnchors + score.missedAnchors})`
}

function compactSpans(spans) {
  if (!Array.isArray(spans) || !spans.length) return 'none'
  return spans.map((span) => `${span.on} -> ${span.off}`).join('; ')
}

function compactTransitions(transitions) {
  if (!Array.isArray(transitions) || !transitions.length) return 'none'
  return transitions.map((row) => `${row.event} ${row.date}`).join(', ')
}

export function formatCarsCalibrationRunDate(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return 'unknown-date'
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function buildCarsCalibrationFilename(symbols, date = new Date()) {
  const stem = (Array.isArray(symbols) ? symbols : [])
    .map((symbol) => String(symbol || '').trim().toUpperCase())
    .filter(Boolean)
    .join('-') || 'symbols'
  return `${formatCarsCalibrationRunDate(date)}-cars-calibration-${stem}.json`
}

export async function saveTextFile(contents, filename) {
  const blob = new Blob([contents], { type: 'application/json' })

  if (typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function') {
    const handle = await window.showSaveFilePicker({
      suggestedName: filename,
      types: [{
        description: 'JSON',
        accept: { 'application/json': ['.json'] },
      }],
    })
    const writable = await handle.createWritable()
    await writable.write(blob)
    await writable.close()
    return
  }

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function CarsCalibrationPanel({ compact = false, initialSymbols = CARS_CALIBRATION_TICKERS }) {
  const initialSymbolsText = useMemo(() => {
    return formatSymbolsText(initialSymbols) || CARS_CALIBRATION_TICKERS.join(', ')
  }, [initialSymbols])
  const [symbolsText, setSymbolsText] = useState(initialSymbolsText)
  const [symbolsTouched, setSymbolsTouched] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  const [results, setResults] = useState([])
  const [copied, setCopied] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!symbolsTouched) setSymbolsText(initialSymbolsText)
  }, [initialSymbolsText, symbolsTouched])

  const symbols = useMemo(() => parseSymbols(symbolsText), [symbolsText])
  const sortedResults = useMemo(() => {
    return [...results].sort((a, b) => {
      const aScore = a.score?.total ?? Number.POSITIVE_INFINITY
      const bScore = b.score?.total ?? Number.POSITIVE_INFINITY
      if (a.symbol !== b.symbol) return a.symbol.localeCompare(b.symbol)
      return aScore - bScore
    })
  }, [results])

  const summaryJson = useMemo(() => JSON.stringify({
    symbols,
    variants: CARS_CALIBRATION_VARIANTS.map((variant) => variant.label),
    diagnosticVariants: CARS_CALIBRATION_DIAGNOSTIC_VARIANTS,
    anchors: CARS_CALIBRATION_ANCHORS,
    results: sortedResults.map((result) => ({
      symbol: result.symbol,
      variant: result.variant,
      presetParams: result.presetParams,
      score: result.score,
      inputAudit: result.inputAudit,
      transitions: result.transitions,
      spans: result.spans,
      ...(CARS_CALIBRATION_DIAGNOSTIC_VARIANTS.includes(result.variant)
        ? { diagnostic: buildCarsCalibrationDiagnosticRows(result.rows, CARS_CALIBRATION_ANCHORS[result.symbol]) }
        : {}),
    })),
  }, null, 2), [sortedResults, symbols])

  async function handleRun() {
    setRunning(true)
    setError('')
    setCopied(false)
    setSaved(false)
    setResults([])

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Missing Supabase session')
      const nextResults = await runCarsCalibration({
        symbols,
        variants: CARS_CALIBRATION_VARIANTS,
        anchorsBySymbol: CARS_CALIBRATION_ANCHORS,
        token: session.access_token,
      })
      setResults(nextResults)
    } catch (err) {
      setError(err?.message || 'CA|RS calibration failed')
    } finally {
      setRunning(false)
    }
  }

  async function handleCopy() {
    await navigator.clipboard?.writeText(summaryJson)
    setCopied(true)
  }

  async function handleSave() {
    try {
      await saveTextFile(summaryJson, buildCarsCalibrationFilename(symbols))
      setSaved(true)
    } catch (err) {
      if (err?.name === 'AbortError') return
      setSaved(false)
      setError(err?.message || 'Failed to save calibration JSON')
    }
  }

  const resultMaxHeight = compact ? 'h-full max-h-full' : 'max-h-[calc(100vh-240px)]'
  const variantsClass = compact
    ? 'grid grid-cols-2 gap-x-3 gap-y-1 max-h-32 overflow-y-auto overflow-x-hidden pr-1 text-[10px] font-mono text-inkMuted'
    : 'space-y-1 text-xs font-mono text-inkMuted'

  return (
    <div className={compact ? 'h-full min-h-0 flex flex-col gap-3' : 'space-y-4'}>
      <header className={`flex flex-col gap-3 border-b border-border pb-4 ${compact ? '' : 'lg:flex-row lg:items-end lg:justify-between'}`}>
        <div>
          <p className="label mb-2">Internal</p>
          <h1 className={compact ? 'text-lg font-bold text-ink' : 'text-2xl font-bold text-ink'}>CA|RS Calibration</h1>
          <p className="text-sm text-inkMuted max-w-3xl mt-2">
            Runs adjusted daily primary bars against adjusted SPY through the engine proxy and scores returned regime spans against TradingView anchors.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-2"
            onClick={handleCopy}
            disabled={!results.length}
          >
            <Copy size={16} />
            {copied ? 'Copied' : 'Copy JSON'}
          </button>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-2"
            onClick={handleSave}
            disabled={!results.length}
          >
            <Download size={16} />
            Save JSON
          </button>
          <ActionSuccessToast message={saved ? 'Calibration saved' : null} onDismiss={() => setSaved(false)} />
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            onClick={handleRun}
            disabled={running || !symbols.length}
          >
            {running ? <RefreshCw size={16} className="animate-spin" /> : <Play size={16} />}
            {running ? 'Running' : 'Run Calibration'}
          </button>
        </div>
      </header>

      <section className={compact ? 'flex-1 min-h-0 flex flex-col gap-3' : 'grid gap-4 lg:grid-cols-[minmax(260px,340px),1fr]'}>
        <aside className={compact ? 'space-y-3' : 'card space-y-4'}>
          <label className="block">
            <span className="label block mb-2">Tickers</span>
            <textarea
              className="input-field min-h-[90px]"
              value={symbolsText}
              onChange={(event) => {
                setSymbolsTouched(true)
                setSymbolsText(event.target.value)
              }}
              data-testid="cars-calibration-symbols"
            />
          </label>
          <div className="min-w-0">
            <p className="label mb-2">Variants</p>
            <div className={variantsClass}>
              {CARS_CALIBRATION_VARIANTS.map((variant) => (
                <div key={variant.label} className="truncate" title={variant.label}>{variant.label}</div>
              ))}
            </div>
          </div>
          <div>
            <p className="label mb-2">Anchors Loaded</p>
            <div className="space-y-1 text-xs font-mono text-inkMuted">
              {Object.entries(CARS_CALIBRATION_ANCHORS).map(([symbol, anchors]) => (
                <div key={symbol}>{symbol}: {anchors.length}</div>
              ))}
            </div>
          </div>
        </aside>

        <main className={`${compact ? 'flex-1 min-h-0' : 'card'} p-0 overflow-hidden border border-border rounded-xl`}>
          {error ? (
            <div className="p-4 text-sm text-ember" data-testid="cars-calibration-error">{error}</div>
          ) : null}
          {!results.length && !running ? (
            <div className="p-6 text-sm text-inkMuted">
              Run calibration to generate compact transition tables and span scores.
            </div>
          ) : null}
          {running ? (
            <div className="p-6 text-sm text-inkMuted">Calling adjusted candle and engine proxies...</div>
          ) : null}
          {sortedResults.length ? (
            <div className={`overflow-auto ${resultMaxHeight}`}>
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-canvas2 text-inkMuted">
                  <tr>
                    <th className="px-3 py-2 font-mono">Symbol</th>
                    <th className="px-3 py-2 font-mono">Variant</th>
                    <th className="px-3 py-2 font-mono">Score</th>
                    <th className="px-3 py-2 font-mono">Input</th>
                    <th className="px-3 py-2 font-mono">Transitions</th>
                    <th className="px-3 py-2 font-mono">ON Spans</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedResults.map((result) => (
                    <tr
                      key={`${result.symbol}-${result.variant}`}
                      className="border-t border-border align-top"
                      data-testid="cars-calibration-result"
                    >
                      <td className="px-3 py-2 font-bold">{result.symbol}</td>
                      <td className="px-3 py-2 font-mono">{result.variant}</td>
                      <td className="px-3 py-2 font-mono whitespace-nowrap">{formatScore(result.score)}</td>
                      <td className="px-3 py-2 font-mono text-inkMuted whitespace-nowrap">
                        {result.inputAudit.primaryBars}/{result.inputAudit.benchmarkBars} bars
                      </td>
                      <td className="px-3 py-2 font-mono min-w-[260px]">{compactTransitions(result.transitions)}</td>
                      <td className="px-3 py-2 font-mono min-w-[320px]">{compactSpans(result.spans)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </main>
      </section>
    </div>
  )
}

export default function CarsCalibration() {
  return (
    <div className="p-4 sm:p-6 min-h-screen">
      <div className="max-w-[1500px] mx-auto">
        <CarsCalibrationPanel />
      </div>
    </div>
  )
}
